import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { memberAuthAPI } from '../utils/api';

const MemberAuthContext = createContext(null);

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('jnaft_member_token');
    if (!token) {
      // No token — not logged in, nothing to do
      setLoading(false);
      return;
    }

    // Token exists — silently verify it
    // Use a raw fetch so the axios interceptor can't trigger a redirect
    fetch('/api/member/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setMember(data.member);
        } else {
          // Token invalid or expired — clear it silently, no redirect
          localStorage.removeItem('jnaft_member_token');
        }
      })
      .catch(() => {
        // Backend not running — clear bad token, don't redirect
        localStorage.removeItem('jnaft_member_token');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await memberAuthAPI.login({ email, password });
    localStorage.setItem('jnaft_member_token', data.token);
    setMember(data.member);
    return data.member;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('jnaft_member_token');
    setMember(null);
  }, []);

  const refreshMember = useCallback(async () => {
    try {
      const { data } = await memberAuthAPI.getMe();
      setMember(data.member);
    } catch {
      // Silently fail — don't redirect
    }
  }, []);

  return (
    <MemberAuthContext.Provider value={{ member, loading, login, logout, refreshMember }}>
      {children}
    </MemberAuthContext.Provider>
  );
};

export const useMemberAuth = () => useContext(MemberAuthContext);
